/**
 * Pure read/write helpers for the openroles block's two builder-only fields.
 *
 * Both live in `config` once persisted. `serializeBlocks` emits only
 * `{id, type, enabled, order, customTitle, customSubtitle, config}`, and
 * `setBlocks` runs it on every write — so a top-level key written by an editor
 * is gone by the very next render, and by every fresh load (the API only ever
 * returns `config`). Read from either place; write to both.
 *
 * They live here rather than in `useCompanyOpenRoles` so they can be unit
 * tested without dragging in the expo hook chain.
 */
import type {
  PublicProfileAnalyticsBlockBreakdown,
  PublicProfileAnalyticsSourceBreakdown,
} from '@tabbio/types';
import type {
  PublicProfileBlock,
  PublicProfileOpenRolesLayout,
} from './public-profile-builder';

/** Reads the "Collect CVs for future roles" flag. Defaults to enabled. */
export function isTalentPoolEnabled(
  block: PublicProfileBlock | null | undefined,
): boolean {
  if (typeof block?.talentPoolEnabled === 'boolean') {
    return block.talentPoolEnabled;
  }
  if (typeof block?.config?.talentPoolEnabled === 'boolean') {
    return block.config.talentPoolEnabled;
  }
  return true;
}

/** Writes the talent-pool flag to both places so it survives serialization. */
export function withTalentPoolEnabled<T extends PublicProfileBlock>(
  block: T,
  value: boolean,
): T {
  return {
    ...block,
    talentPoolEnabled: value,
    config: { ...(block.config ?? {}), talentPoolEnabled: value },
  };
}

/**
 * Reads the open-roles layout choice. `config.rolesLayout` is the canonical
 * key — it is what the API validates and what the public profile renders from.
 */
export function getOpenRolesLayout(
  block: PublicProfileBlock | null | undefined,
): PublicProfileOpenRolesLayout {
  return block?.rolesLayout ?? block?.config?.rolesLayout ?? 'tabs';
}

/** Writes the layout to both places so it survives serialization. */
export function withOpenRolesLayout<T extends PublicProfileBlock>(
  block: T,
  value: PublicProfileOpenRolesLayout,
): T {
  return {
    ...block,
    rolesLayout: value,
    config: { ...(block.config ?? {}), rolesLayout: value },
  };
}

/**
 * Reads the open-roles thumbnail. `config.imageUrl` is the canonical key — it
 * is what the API sanitizer keeps and what `buildPublicProfileRenderBlocks`
 * copies onto the render block's `imageUrl`.
 */
export function getOpenRolesThumbnail(
  block: PublicProfileBlock | null | undefined,
): string | null {
  const value = block?.config?.imageUrl ?? block?.thumbnailUrl ?? null;
  const trimmed = typeof value === 'string' ? value.trim() : '';
  return trimmed || null;
}

/** Writes the thumbnail to both places so it survives serialization. */
export function withOpenRolesThumbnail<T extends PublicProfileBlock>(
  block: T,
  value: string | null,
): T {
  const normalized = value?.trim() ? value.trim() : null;
  return {
    ...block,
    thumbnailUrl: normalized,
    config: { ...(block.config ?? {}), imageUrl: normalized },
  };
}

/**
 * Clicks recorded against this specific openroles block. `blockClicks` is the
 * only block-scoped figure the analytics summary carries — views and every
 * other total are page-wide, because no per-block view event exists.
 */
export function getOpenRolesBlockClicks(
  blockClicks: PublicProfileAnalyticsBlockBreakdown[] | undefined,
  blockId: string | null | undefined,
): number {
  if (!blockClicks?.length) return 0;
  return blockClicks.reduce((total, entry) => {
    const matchesId = Boolean(blockId) && entry.blockId === blockId;
    // Older events were written before `blockId` was captured, so fall back to
    // the block type — there is only ever one openroles block per page.
    const matchesType = !entry.blockId && entry.blockType === 'openroles';
    return matchesId || matchesType ? total + entry.count : total;
  }, 0);
}

/**
 * View-to-apply rate, or `null` when the ratio is not trustworthy.
 *
 * Apply clicks come from the server-side redirect (every hit, bots included);
 * views come from a client-side pixel that ad-blockers drop and that never
 * fires for bots or SSR. When applies outrun views the pixel has clearly
 * under-counted, and any percentage would be fiction — so the caller shows a
 * dash instead.
 */
export function getViewToApplyRate(
  views: number,
  jobApplyClicks: number,
): number | null {
  if (!Number.isFinite(views) || !Number.isFinite(jobApplyClicks)) return null;
  if (views <= 0 || jobApplyClicks < 0) return null;
  if (jobApplyClicks > views) return null;
  return (jobApplyClicks / views) * 100;
}

export interface OpenRolesSourceShare extends PublicProfileAnalyticsSourceBreakdown {
  /** Percentage of all recorded events for the range, 0-100. */
  share: number;
}

/**
 * Top traffic sources with their share of the whole range — the share divides
 * by every source's count, not just the returned slice, so the bars stay
 * honest when the tail is truncated.
 */
export function getTopSourceShares(
  sources: PublicProfileAnalyticsSourceBreakdown[] | undefined,
  limit = 5,
): OpenRolesSourceShare[] {
  if (!sources?.length) return [];
  const total = sources.reduce((sum, entry) => sum + entry.count, 0);
  return [...sources]
    .sort((left, right) => right.count - left.count)
    .slice(0, Math.max(limit, 0))
    .map((entry) => ({
      ...entry,
      share: total > 0 ? (entry.count / total) * 100 : 0,
    }));
}
